import React, { FC, useEffect, useState } from 'react';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { permissions } from 'utils/permission';
import { Order } from 'models/order';
import { OrderInputType } from 'models/order-input/shared';
import SupplierOrder from './supplier-order.component';
import { SupplierOrderProvider } from './supplier-order-context';
import { SupplierOrderInput } from 'models/order-input/supplier-order';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { restorationAsInput } from 'components/order-flow/shared/restoration-input-helper';
import { formatPinNumber } from 'utils/personal-identity-number';
import { orderForSimpleModifyTemplate } from 'apollo/templates/order/query.graphql';
import { extractOrderInputType } from 'utils/orders';

const ModifySupplierOrder: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const [order, setOrder] = useState<SupplierOrderInput>({} as SupplierOrderInput);
    const [parentOrder, setParentOrder] = useState<Order['parentOrder']>();
    const params = useParams<{ id: string }>();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();

    const [fetchOrder, { loading }] = useLazyQuery(orderForSimpleModifyTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ order }: { order: Order }) => {
            if (order) {
                if (order.type !== 'SIMPLE_TYPE') {
                    throw new Error('Order is not a simple order');
                }

                setParentOrder(order.parentOrder);
                setOrder({
                    inputType: extractOrderInputType(order),
                    isWhiteLabeled: permissions.isWhiteLabeledOrganization(currentOrg),
                    _id: order._id,
                    supplier: order.supplier,
                    customer: order.customer,
                    dentist: order.dentist,
                    // DD-3484: an edit of a sub-order whose patient was withheld must keep
                    // withholding it, not overwrite it with an empty patient.
                    withholdPatient: !order.patient,
                    // DD-3494: a patient-less order prefills no patient, as a fresh wizard starts.
                    patient: order.patient
                        ? {
                              personalIdentityNumber: formatPinNumber(order.patient.personalIdentityNumber),
                              firstName: order.patient.firstName,
                              lastName: order.patient.lastName,
                              noPersonalIdentityNumber: order.patient.noPersonalIdentityNumber || false,
                          }
                        : undefined,
                    restorations: (order.restorations ?? []).map(restorationAsInput),
                    comment: '',
                    historicalComments: order.historicalComments || [],
                    participants: {
                        linkedDentists: order.linkedDentists || [],
                        linkedClinics: order.linkedClinics || [],
                        customParticipantEmails: order.customParticipantEmails || [],
                    },
                    files: order.filesUrl || [],
                    sample: order.deliveryDetails.sample || { type: '', content: [], otherContent: '' },
                    deliveryDetails: {
                        date: order.deliveryDetails.date ? new Date(order.deliveryDetails.date) : null,
                        time: {
                            hour: order.deliveryDetails.time?.hour,
                            minute: order.deliveryDetails.time?.minute,
                        },
                        pickUpLocation: order.deliveryDetails.pickUpLocation,
                        destination: order.deliveryDetails.destination,
                    },
                });
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (params.id) {
            fetchOrder({ variables: { orderId: params.id } });
        }
    }, [params.id]);

    if (loading || !order._id) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Box>
                    <CircularProgress size={80} />
                </Box>
            </Box>
        );
    }

    // DD-3484: this flow knows nothing of the parent link or of what was selected to
    // travel, so a sub-order is not edited here (the backend refuses it as well).
    // `parentOrder` resolves only for the lab that subcontracted the order.
    if (parentOrder) {
        return (
            <Box sx={{ maxWidth: '1200px' }} mx="auto" mb={2}>
                <Alert severity="info" data-test="modify-supplier-order.sub-order-not-editable">
                    {translate('orders:SUB_ORDER_NOT_EDITABLE', { orderId: parentOrder.orderId })}
                </Alert>
            </Box>
        );
    }

    return (
        <SupplierOrderProvider initialOrder={order}>
            <SupplierOrder />
        </SupplierOrderProvider>
    );
};

export default ModifySupplierOrder;
